import {
  APP_INITIALIZER,
  FactoryProvider,
  inject,
  provideAppInitializer,
} from "@angular/core";
import { AuthService } from "./shared/auth/auth.service";
import { Store } from "@ngrx/store";
import { AppState } from "./app.reducer";
import {
  AuthApiLoginResponse,
  AuthApiPermissionCodeListResponse,
} from "./shared/auth/auth-api.types";
import { AuthUser } from "./shared/auth/models/auth-user.model";
import { AuthSite } from "./shared/auth/models/auth-logspace-locator.model";
import { AuthActions } from "./shared/auth/auth.actions";
import { AppInitResolverService } from "./shared/services/app-init-resolver.service";
import { SiteStorageService } from "./shared/auth/services/site-storage.service";
import { Router } from "@angular/router";
import { catchError, of, tap, switchMap, timeout } from "rxjs";

export function initAppFactory(
  authService: AuthService,
  store: Store<AppState>,
  siteStorage: SiteStorageService,
  appInitResolver: AppInitResolverService,
  router: Router,
): () => Promise<any> {
  // load the current user specified in the server cookie from the server
  // if there is a current user, show home page
  // if there is not current user, show login page
  // return a promise
  return () => {
    return new Promise<any>((resolve, reject) => {
      appInitResolver.setResolver(resolve, reject);

      authService
        .loadCurrentUser()
        .pipe(
          timeout(5000), // Prevent indefinite blocking on slow API
          tap((resp: AuthApiLoginResponse | null) => {
            if (!resp) {
              store.dispatch(AuthActions.loadCurrentUserFailure({}));
              router.navigate(["/login"]);
              return;
            }

            const user: AuthUser = {
              userId: resp.data.userId,
              firstName: resp.data.firstName,
              lastName: resp.data.lastName,
              username: resp.data.username,
              displayName: resp.data.displayName,
              email: resp.data.email,
              imageFileName: resp.data.imageFileName,
            };
            // Handle new API structure
            const sites = resp.data.sites || [];

            const authSites = sites.map(
              (apiSite) =>
                ({
                  siteId: apiSite.siteId,
                  siteName: apiSite.siteName,
                  siteType: apiSite.siteType,
                  siteAddress: apiSite.siteAddress,
                  userRoleId: apiSite.userRoleId,
                  userRoleName: apiSite.userRoleName,
                  status: apiSite.status,
                  isParent: apiSite.isParent,
                  parentSiteId: apiSite.parentSiteId,
                }) as AuthSite,
            );

            //
            // Determine selected site from API response or fallback to local storage
            // Priority: 1. API selectedSiteId (from database session), 2. Local storage, 3. Auto-select single site
            let selectedSite: AuthSite | null = null;

            // First, check if API returned a selectedSiteId (from database session)
            if (resp.data.selectedSiteId) {
              for (const site of authSites) {
                if (site.siteId === resp.data.selectedSiteId) {
                  selectedSite = site;
                  break;
                }
              }
            }

            // Fallback: Read existing site selection from local storage
            if (!selectedSite) {
              const storedData = siteStorage.readSiteData();
              if (storedData?.selectedSiteId) {
                for (const site of authSites) {
                  if (site.siteId === storedData.selectedSiteId) {
                    selectedSite = site;
                    break;
                  }
                }
              }
            }

            // Auto-select if user has exactly one site
            if (!selectedSite && authSites.length === 1) {
              selectedSite = authSites[0];
            }

            siteStorage.storeSiteData(
              authSites,
              !!selectedSite ? selectedSite.siteId : null,
            );

            store.dispatch(
              AuthActions.loadCurrentUserSuccess({
                user,
                site: selectedSite,
                siteList: authSites,
              }),
            );

            // Load permissions if there's a selected site
            if (selectedSite) {
              store.dispatch(
                AuthActions.updateSite({
                  user,
                  newSite: selectedSite,
                }),
              );
            }
          }),
          catchError((err) => {
            // Distinguish "no session" from "transient API failure":
            //   - 401 is already surfaced as `null` by AuthService.loadCurrentUser
            //     and handled by the !resp branch in the tap above — we
            //     won't reach this catch for that case.
            //   - Anything that lands here is a transient failure (network
            //     error, 5xx, timeout from the timeout(5000) operator).
            //     Don't dispatch loadCurrentUserFailure (which nulls out
            //     user/site/permissions in the reducer) and don't force a
            //     /login redirect. The app boots; route guards still gate
            //     protected URLs naturally, but a logged-in user isn't
            //     destructively kicked out on a network blip.
            console.error(
              "[AUTH] /auth/me transient failure during app init — not clearing session:",
              err,
            );
            return of(null);
          }),
        )
        .subscribe({
          next: () => resolve(true),
          error: () => resolve(true), // Even on error, we resolve to prevent app from hanging
          complete: () => resolve(true),
        });

      // authService
      //   .getPermissions(site.siteId ?? "", user.userId ?? "")
      //   .then((resp: AuthApiPermissionCodeListResponse) => {
      //     store.dispatch(
      //       AuthActions.loadCurrentUserSuccess({
      //         user,
      //         site,
      //         permissions: resp.data,
      //       }),
      //     );
      //     resolve(true);
      //   })
      //   .catch((err) => {
      //     store.dispatch(
      //       AuthActions.loadCurrentUserFailure({ error: err }),
      //     );
      //     resolve(true);
      //   });
    });
  };
}

export const appInitProvider = provideAppInitializer(() => {
  const initializerFn = initAppFactory(
    inject(AuthService),
    inject(Store),
    inject(SiteStorageService),
    inject(AppInitResolverService),
    inject(Router),
  );
  return initializerFn();
});
